import type { HitV1 } from '@ttab/elephant-api/index'
import type { Block } from '@ttab/elephant-api/newsdoc'
import { memoTemplates } from '../index.js'
import { plainText } from './plainText.js'

/**
 * A PM that already exists, ready to be folded into one being written.
 *
 * "Nattens fem" goes out on its own before the morning PM, and its content then
 * has to reach the morning PM without being typed a second time. The creation
 * dialog offers the day's editorial-info documents by `title` and inserts the
 * `blocks` of whichever the editor picks.
 *
 * The blocks are the source document's own, carried rather than re-rendered, so
 * that what a memo ends up saying is what the PM it was taken from said -
 * inline formatting a rendered memo line cannot express included.
 */
export interface InsertedMemoDocument {
  id: string
  title: string
  /**
   * The source document's `core/text` content, its heading-1 already dropped.
   * Blank blocks are kept between lines, since they are how the PM was spaced.
   */
  blocks: Block[]
}

/**
 * An inserted PM as the memo being written carries it: in one of the memo's own
 * blocks, and holding only the lines the editor kept.
 *
 * Which block is the editor's answer to the list's own question, the way a
 * selected `MemoItem` carries the block it was put in - a PM restating the
 * night's five belongs under the memo's own heading for them, not in a run of
 * its own above everything.
 */
export interface InsertedMemo extends InsertedMemoDocument {
  blockId: string
}

/**
 * The sign-offs the shipped templates write, by what they say.
 *
 * Which template a published PM came out of is not recorded on the document, so
 * what it says is what a sign-off can be recognised by. That is sound because a
 * postamble is the template's own copy rather than anything the editor typed -
 * but it does mean a sign-off reworded on the published PM itself is not
 * recognised, and the editor drops that line by hand the way they can any other.
 */
const POSTAMBLES = new Set(
  memoTemplates.flatMap((template) => template.postamble.map((text) => plainText(text)))
)

/** Whether a block says nothing - a blank line of the PM it was read from. */
export function isBlankBlock(block: Block): boolean {
  return !plainText(block.data.text ?? '')
}

/**
 * The blocks with the source PM's own sign-off dropped. A PM folded into another
 * brings its content, not its footer: the night's five signs off pointing at the
 * morning PM, which is the very memo it is folded into, and the memo being
 * written adds a sign-off of its own below everything anyway.
 *
 * Only a trailing run of them goes, which is where a sign-off is written, so a
 * line that happens to repeat one further up is left alone. The blank lines
 * spacing the sign-off off go with it.
 */
function withoutPostamble(blocks: Block[]): Block[] {
  let end = blocks.length

  while (end > 0 && (
    isBlankBlock(blocks[end - 1])
    || POSTAMBLES.has(plainText(blocks[end - 1].data.text ?? ''))
  )) {
    end -= 1
  }

  return blocks.slice(0, end)
}

/**
 * The blocks with every run of blank ones collapsed into one, and none left at
 * either end - one blank line is the gap between two lines, and it takes a line
 * on both sides to make one.
 */
function withSingleGaps(blocks: Block[]): Block[] {
  const kept: Block[] = []
  let gap: Block | undefined

  for (const block of blocks) {
    if (isBlankBlock(block)) {
      gap = kept.length ? (gap ?? block) : undefined
      continue
    }

    if (gap) {
      kept.push(gap)
      gap = undefined
    }

    kept.push(block)
  }

  return kept
}

/**
 * Read an index hit - queried with `loadDocument`, so the whole newsdoc is
 * there - as a document that can be inserted into a memo.
 *
 * The heading-1 is left out: it is the source PM's own headline, and the memo
 * being written already has one of its own. So is everything that is not
 * `core/text`: a memo is text, and carrying an image or a factbox would put
 * something in the created document that the preview never showed the editor.
 * The source PM's own sign-off goes last - see `withoutPostamble`. Blank text
 * blocks are kept as the PM's spacing, one per gap - see `withSingleGaps`.
 *
 * A hit with no title, or with nothing left to insert once that is done, yields
 * nothing rather than an untitled or empty entry in the dialog's list.
 */
export function toInsertedMemoDocument(hit: HitV1): InsertedMemoDocument | undefined {
  const title = hit.document?.title.trim()

  if (!title) {
    return undefined
  }

  const blocks = withSingleGaps(withoutPostamble((hit.document?.content ?? []).filter((block) => (
    block.type === 'core/text'
    && block.role !== 'heading-1'
  ))))

  if (!blocks.length) {
    return undefined
  }

  return { id: hit.id, title, blocks }
}
